/* eslint-disable jsx-a11y/no-distracting-elements */
import { FC, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { SmartPremiumIcon, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { ESubscription, FeaturesContext } from 'features';
import { useShellVersion } from 'hooks';
import { LocalizationContext } from 'localization';
import { IVersionsResponse, VERSIONS } from 'schemas';

import { SubscriptionLabel } from '../SubscriptionLabel';
import { ModalDept } from './components';
import { USE_DEBT_QUERY } from './schema';
import s from './Version.module.scss';

interface IVersionProps {
  showOnTop?: boolean;
}
export const Version: FC<IVersionProps> = ({ showOnTop }) => {
  const {
    data: { tokens, permissions },
  } = useContext(AuthContext);
  const { subscription, expiresAt, it_adviser_support } =
    useContext(FeaturesContext);
  const { t } = useContext(LocalizationContext);
  const shellVersion = useShellVersion();

  const { data } = useQuery<IVersionsResponse>(VERSIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: debt } = useQuery(USE_DEBT_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 3600000,
  });

  const [counter, setCounter] = useState(0);
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const [isModalDept, setIsModalDept] = useState(false);
  const [isShowFrame, setIsShowFrame] = useState<boolean>(true);

  useEffect(() => {
    if (counter > 4) {
      setShowEasterEgg(true);
    }
  }, [counter]);

  useEffect(() => {
    if (debt?.canUseDebt) {
      setIsShowFrame(true);
    } else {
      setIsShowFrame(false);
    }
  }, [debt?.canUseDebt]);

  const { versions } = data ?? {};

  const encodedToken = atob(tokens?.access_token?.split('.')[1] ?? '');
  const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

  return (
    <>
      {isModalDept && <ModalDept onClose={() => setIsModalDept(false)} />}
      <Tooltip
        className={s.tooltipContainer}
        contentClassName={cn(s.tooltip, !showOnTop && s.rightPosition)}
        content={
          <div className={s.tooltipContent}>
            <div className={cn(s.header, s.row)}>{t('version.about')}</div>
            <div className={s.row}>
              <div>{t('version.frontend')}</div>
              <div>{process.env.REACT_APP_VERSION}</div>
            </div>
            <div className={s.row}>
              <div>{t('version.backend')}</div>
              <div>{versions?.backend}</div>
            </div>
            {shellVersion && (
              <div className={s.row}>
                <div>{t('shell.version')}</div>
                <div>{shellVersion}</div>
              </div>
            )}

            {clubId && (
              <div className={s.row}>
                <div>Club ID</div>
                <div>{clubId}</div>
              </div>
            )}
            {subscription &&
              (subscription === ESubscription.TRIAL ? (
                <div className={s.trial}>
                  <span className={s.trialHeader}>
                    {t('subscription.trial.title')}
                  </span>
                  <span className={s.trialDescription}>
                    {t('subscription.trial.description')}
                  </span>
                  <div className={s.trialExpires}>
                    {t('subscription.until')}
                    <span>{dayjs(expiresAt).format('DD MMM YYYY')}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className={cn(s.header, s.row)}>
                    {t('subscription.info')}
                  </div>
                  <div className={s.row}>
                    {subscription}
                    <SubscriptionLabel variant="status" />
                  </div>
                  {subscription !== ESubscription.FREE && expiresAt && (
                    <div className={s.row}>
                      {t('subscription.until')}
                      <span>{dayjs(expiresAt).format('DD MMM YYYY')}</span>
                    </div>
                  )}
                </>
              ))}
            {permissions?.has('can_use_debt') && isShowFrame && (
              <div className={s.dept}>
                <span className={s.dept_title}>
                  {t('version.tooltip.debt.title')}
                </span>
                <span className={s.dept_text}>
                  {t('version.tooltip.debt.subtitle')}
                </span>
                <button
                  onClick={() => setIsModalDept(true)}
                  className={s.dept_button}
                >
                  {t('more')}
                </button>
              </div>
            )}
            <>
              {it_adviser_support ? (
                <>
                  <div className={s.row}>
                    <div className={s.left}>
                      <SmartPremiumIcon />
                      <span className={s.text}>SmartPremium</span>
                    </div>
                    <div className={s.right}>{t('online')}</div>
                  </div>
                  <div className={s.smart_tooltip}>
                    <img
                      src="/images/smartPremium_tooltip.png"
                      className={s.image}
                      alt={'smart premium'}
                    />
                    <span className={s.title}>SmartPremium</span>
                    <span className={s.text}>{t('smart.premium.text')}</span>
                    <a
                      href="https://smartshell.gg/smartpremium/?utm_source=pannel&utm_campaign=banner&utm_content=upravlenia&utm_term=administrator"
                      className={s.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('more')}
                    </a>
                  </div>
                </>
              ) : (
                <div className={s.smart_tooltip}>
                  <img
                    src="/images/smartPremium_tooltip.png"
                    className={s.image}
                    alt={'smart premium'}
                  />
                  <span className={s.title}>SmartPremium</span>
                  <span className={s.text}>{t('smart.premium.text')}</span>
                  <a
                    href="https://smartshell.gg/smartpremium/?utm_source=pannel&utm_campaign=banner&utm_content=upravlenia&utm_term=administrator"
                    className={s.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('more')}
                  </a>
                </div>
              )}
            </>
          </div>
        }
        position={showOnTop ? 'top' : 'right-bottom'}
      >
        <div
          onClick={() => setCounter(counter + 1)}
          className={s.marqueeContainer}
        >
          {/* @ts-ignore for roma */}
          {showEasterEgg && <marquee>{versions?.easter_egg}</marquee>}

          <div className={s.footer}>
            <div>{subscription && <SubscriptionLabel />}</div>
            {it_adviser_support && (
              <div className={s.smart}>
                <SmartPremiumIcon />
                <span className={s.text}>SmartPremium</span>
              </div>
            )}
          </div>
        </div>
      </Tooltip>
    </>
  );
};
